import React from 'react';
import { Table, Label, Icon, Segment, Header, Message } from 'semantic-ui-react';
import { RankingEntryDto } from '../../types/campaigns.types';
import GlassmorphismTheme from '../../styles/glassmorphism';

interface CampaignRankingCardProps {
  entries: RankingEntryDto[];
  title: string;
  subtitle?: string;
  showTeamColumn?: boolean;
}

const CampaignRankingCard: React.FC<CampaignRankingCardProps> = ({
  entries,
  title,
  subtitle,
  showTeamColumn = false,
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Acima':
        return 'green';
      case 'NaMeta':
        return 'yellow';
      case 'Abaixo':
        return 'red';
      default:
        return 'grey';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Acima':
        return 'arrow up';
      case 'NaMeta':
        return 'check';
      case 'Abaixo':
        return 'arrow down';
      default:
        return 'question';
    }
  };

  const getPositionIcon = (position: number) => {
    if (position === 1) return '🏆';
    if (position === 2) return '🥈';
    if (position === 3) return '🥉';
    return null;
  };

  if (entries.length === 0) {
    return (
      <Segment style={GlassmorphismTheme.components.glassCardDark}>
        <Header as="h3" style={{ color: GlassmorphismTheme.colors.solid.textLight, marginBottom: '1rem' }}>
          {title}
        </Header>
        {subtitle && (
          <p style={{ color: GlassmorphismTheme.colors.solid.textLight, opacity: 0.8, marginBottom: '1rem' }}>
            {subtitle}
          </p>
        )}
        <Message info>
          <Message.Header>Nenhum dado disponível</Message.Header>
          <p>Não há participantes no ranking no momento.</p>
        </Message>
      </Segment>
    );
  }

  return (
    <Segment style={GlassmorphismTheme.components.glassCardDark}>
      <Header as="h3" style={{ color: GlassmorphismTheme.colors.solid.textLight, marginBottom: '1rem' }}>
        {title}
      </Header>
      {subtitle && (
        <p style={{ color: GlassmorphismTheme.colors.solid.textLight, opacity: 0.8, marginBottom: '1rem' }}>
          {subtitle}
        </p>
      )}
      <div style={{ overflowX: 'auto' }}>
        <Table celled striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell style={{ color: '#000000', fontWeight: 'bold', width: '60px' }}>
                Pos.
              </Table.HeaderCell>
              <Table.HeaderCell style={{ color: '#000000', fontWeight: 'bold' }}>
                Participante
              </Table.HeaderCell>
              {showTeamColumn && (
                <Table.HeaderCell style={{ color: '#000000', fontWeight: 'bold' }}>
                  Time
                </Table.HeaderCell>
              )}
              <Table.HeaderCell style={{ color: '#000000', fontWeight: 'bold', textAlign: 'right' }}>
                Contratos
              </Table.HeaderCell>
              <Table.HeaderCell style={{ color: '#000000', fontWeight: 'bold', textAlign: 'right' }}>
                Meta
              </Table.HeaderCell>
              <Table.HeaderCell style={{ color: '#000000', fontWeight: 'bold', textAlign: 'right' }}>
                % Atingido
              </Table.HeaderCell>
              <Table.HeaderCell style={{ color: '#000000', fontWeight: 'bold', textAlign: 'center' }}>
                Status
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {entries.map((entry) => (
              <Table.Row key={entry.userId}>
                <Table.Cell style={{ color: '#000000', textAlign: 'center', fontWeight: 'bold' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    {getPositionIcon(entry.posicao) && (
                      <span style={{ fontSize: '1.2em' }}>{getPositionIcon(entry.posicao)}</span>
                    )}
                    <span>{entry.posicao}º</span>
                  </div>
                </Table.Cell>
                <Table.Cell style={{ color: '#000000' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{entry.userNome}</div>
                    <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>
                      {entry.userEmail}
                    </div>
                  </div>
                </Table.Cell>
                {showTeamColumn && (
                  <Table.Cell style={{ color: '#000000' }}>
                    {entry.teamNome || '-'}
                  </Table.Cell>
                )}
                <Table.Cell style={{ color: '#000000', textAlign: 'right', fontWeight: 600 }}>
                  {entry.contratosValidados}
                </Table.Cell>
                <Table.Cell style={{ color: '#000000', textAlign: 'right' }}>
                  {entry.metaIndividual.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </Table.Cell>
                <Table.Cell style={{ color: '#000000', textAlign: 'right' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                    <div
                      style={{
                        width: '60px',
                        height: '8px',
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        borderRadius: '4px',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.min(entry.percentualMetaAtingida, 100)}%`,
                          height: '100%',
                          backgroundColor:
                            entry.percentualMetaAtingida >= 100
                              ? '#2ecc71'
                              : entry.percentualMetaAtingida >= 90
                              ? '#f39c12'
                              : '#e74c3c',
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                    <span style={{ fontWeight: 600, minWidth: '50px' }}>
                      {entry.percentualMetaAtingida.toFixed(1)}%
                    </span>
                  </div>
                </Table.Cell>
                <Table.Cell style={{ textAlign: 'center' }}>
                  <Label color={getStatusColor(entry.status) as any} horizontal>
                    <Icon name={getStatusIcon(entry.status)} />
                    {entry.status}
                  </Label>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    </Segment>
  );
};

export default CampaignRankingCard;

